import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge, STATUS } from '@/components/staffAttendance/Badges';
import { CollarSelect, SectionSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { EsslUpload } from '@/components/staffAttendance/EsslUpload';
import { EsslBackfill } from '@/components/staffAttendance/EsslBackfill';
import { PastAttendanceUpload } from '@/components/staffAttendance/PastAttendanceUpload';
import { matchStaff, SearchNote } from '@/components/staffAttendance/StaffSearch';
import { staffAttApi, errMsg, fmtTime, todayIST } from '@/services/staffAttendanceApi';
import { exportArrayToCsv } from '@/lib/csvExport';

const yesterday = () => { const d = new Date(`${todayIST()}T12:00:00`); d.setDate(d.getDate() - 1); return d.toLocaleDateString('en-CA'); };
const FLAG = {
  missing_in_app: ['Missing in App', 'bg-red-100 text-red-700'], missing_in_essl: ['Missing in eSSL', 'bg-orange-100 text-orange-700'],
  missing_out_app: ['Out missing in App', 'bg-red-100 text-red-700'], missing_out_essl: ['Out missing in eSSL', 'bg-orange-100 text-orange-700'],
  in_gap: ['In gap > 15 min', 'bg-amber-100 text-amber-800'], out_gap: ['Out gap > 15 min', 'bg-amber-100 text-amber-800'],
};
const FILTERS = [['mismatch', 'Mismatches only'], ['all', 'All staff'], ...Object.entries(FLAG).map(([k, [l]]) => [k, l])];

const t = (iso) => (iso ? fmtTime(iso) : '');
const src = (p) => (p ? (p.source === 'essl' ? 'eSSL' : 'App') : '');
const CSV_COLS = [
  ['staff_name', 'Staff Name'], ['staff_id', 'Staff ID'], ['essl_code', 'eSSL Code'], ['section', 'Section'],
  [(r) => t(r.essl_in), 'eSSL First In'], [(r) => t(r.essl_out), 'eSSL Last Out'], ['essl_count', 'eSSL Punches'],
  [(r) => (r.essl_times || []).map(fmtTime).join(' | '), 'eSSL Punch Times'],
  [(r) => t(r.app_in?.time), 'App First In'], [(r) => STATUS[r.app_in?.status]?.label || r.app_in?.status, 'App In Status'], [(r) => src(r.app_in), 'App In Source'],
  [(r) => t(r.app_out?.time), 'App Last Out'], [(r) => STATUS[r.app_out?.status]?.label || r.app_out?.status, 'App Out Status'], [(r) => src(r.app_out), 'App Out Source'],
  [(r) => t(r.app_in?.app_time || r.app_in?.time), 'App Original In'], [(r) => t(r.app_out?.app_time || r.app_out?.time), 'App Original Out'],
  ['app_sessions', 'App Sessions'],
  [(r) => t(r.final_in?.time), 'Final In'], [(r) => src(r.final_in), 'Final In From'], [(r) => t(r.final_out?.time), 'Final Out'], [(r) => src(r.final_out), 'Final Out From'],
  ['late_min', 'Late (min)'], ['early_min', 'Early Leave (min)'], ['hours', 'Hours'], [(r) => (r.changes?.length ? 'Not applied yet' : ''), 'Final Times'], ['result', 'Result'],
].map(([key, header]) => ({ key, header }));

const exportCsv = (date, section, flag, rows, unmatched) => {
  const staff = rows.map((r) => ({ ...r, result: r.flags.length ? r.flags.map((f) => FLAG[f][0]).join(' | ') : 'Matched' }));
  const codes = unmatched.map((u) => ({ staff_name: u.name && u.name !== u.code ? u.name : '', essl_code: u.code, essl_in: u.first, essl_count: u.count, result: 'Machine code not in Staff Master' }));
  const name = ['essl_reconcile', date, section, flag].filter(Boolean).join('_').replace(/[^\w-]+/g, '-');
  exportArrayToCsv([...staff, ...codes], CSV_COLS, `${name}.csv`);
};

const AppPunch = ({ p, testId }) => (p ? (
  <div className="flex items-center gap-1" data-testid={testId}><span className="font-mono">{fmtTime(p.time)}</span><StatusBadge status={p.status} />
    {p.source === 'essl' && <span className="rounded-full bg-indigo-100 text-indigo-700 px-1.5 text-[10px] font-bold">eSSL</span>}
    {p.app_time && p.app_time !== p.time && <span className="text-[10px] text-gray-500" data-testid={`${testId}-orig`}>app {fmtTime(p.app_time)}</span>}</div>
) : <span className="text-gray-300" data-testid={testId}>—</span>);

const Chip = ({ c, children, testId }) => <span className={`rounded-full px-1.5 text-[10px] font-bold ${c}`} data-testid={testId}>{children}</span>;
const Final = ({ r }) => (
  <div data-testid={`essl-final-${r.staff_id}`}>
    <div className="font-mono whitespace-nowrap" title={`In from ${src(r.final_in) || '—'} · Out from ${src(r.final_out) || '—'}`}>{r.final_in ? fmtTime(r.final_in.time) : '—'} → {r.final_out ? fmtTime(r.final_out.time) : '—'}</div>
    <div className="flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
      {r.hours != null && <span data-testid={`essl-hours-${r.staff_id}`}>{r.hours} h</span>}
      {r.late_min ? <Chip c="bg-red-100 text-red-700" testId={`essl-late-${r.staff_id}`}>Late {r.late_min}m</Chip> : null}
      {r.early_min ? <Chip c="bg-orange-100 text-orange-700" testId={`essl-early-${r.staff_id}`}>Early {r.early_min}m</Chip> : null}
      {r.hours == null && r.final_in?.status === 'pending' && <Chip c="bg-slate-100 text-slate-600" testId={`essl-await-${r.staff_id}`}>hours after approval</Chip>}
      {r.changes?.length > 0 && <Chip c="bg-amber-100 text-amber-800" testId={`essl-pending-${r.staff_id}`}>not applied yet</Chip>}
    </div>
  </div>
);

const Row = ({ r, busy, onFill }) => (
  <TableRow data-testid={`essl-row-${r.staff_id}`} className={r.flags.length ? '' : 'opacity-70'}>
    <TableCell><div className="font-medium">{r.staff_name}</div><div className="text-[11px] text-gray-500">{r.staff_id}{r.essl_code ? ` · eSSL ${r.essl_code}` : ''} · {r.section}</div></TableCell>
    <TableCell className="font-mono" data-testid={`essl-machine-${r.staff_id}`} title={r.essl_times.map(fmtTime).join(', ')}>
      {r.essl_in ? fmtTime(r.essl_in) : '—'} → {r.essl_out ? fmtTime(r.essl_out) : '—'}<div className="text-[11px] text-gray-500 font-sans">{r.essl_count} punch{r.essl_count === 1 ? '' : 'es'}</div>
    </TableCell>
    <TableCell><AppPunch p={r.app_in} testId={`essl-app-in-${r.staff_id}`} /></TableCell>
    <TableCell><AppPunch p={r.app_out} testId={`essl-app-out-${r.staff_id}`} />{r.app_sessions > 1 && <div className="text-[11px] text-gray-500">{r.app_sessions} sessions</div>}</TableCell>
    <TableCell><Final r={r} /></TableCell>
    <TableCell data-testid={`essl-flags-${r.staff_id}`}>
      <div className="flex flex-wrap gap-1">{r.flags.length ? r.flags.map((f) => <span key={f} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${FLAG[f][1]}`}>{FLAG[f][0]}</span>)
        : <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold">Matched</span>}</div>
    </TableCell>
    <TableCell className="text-right">
      {r.can_fill && <Button size="sm" disabled={busy} onClick={() => onFill(r)} className="bg-amber-600 hover:bg-amber-700" data-testid={`essl-fill-btn-${r.staff_id}`}>Use eSSL time</Button>}
    </TableCell>
  </TableRow>
);

/** HR tab — day-wise eSSL machine punches vs app attendance. */
export const EsslReconcile = ({ sections, search = '' }) => {
  const [date, setDate] = useState(yesterday());
  const [section, setSection] = useState('');
  const [flag, setFlag] = useState('mismatch');
  const [collar, setCollar] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setData(null);
    staffAttApi.esslReconcile({ date, section: section || undefined, flag, collar: collar || undefined }).then(setData).catch((e) => { toast.error(errMsg(e, 'Could not load')); setData({ rows: [], counts: {}, unmatched: [] }); });
  }, [date, section, flag, collar]);
  useEffect(() => { load(); }, [load]);
  const rows = (data?.rows || []).filter((r) => matchStaff(search, r.staff_name, r.staff_id, r.essl_code));
  const unmatched = (data?.unmatched || []).filter((u) => matchStaff(search, u.code, u.name));

  const fill = async (r) => {
    setBusy(true);
    try {
      const res = await staffAttApi.esslApply(r.staff_id, date);
      toast.success(`${r.staff_name}: ${res.created.map((c) => `${c.label} ${fmtTime(c.time)}`).join(' · ')}`);
      load();
    } catch (e) { toast.error(errMsg(e, 'Could not fill from eSSL')); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="essl-reconcile">
      <EsslUpload onUploaded={(r) => { if (r.date_to) setDate(r.date_to); load(); }} />
      <PastAttendanceUpload />
      <div className="flex flex-wrap items-end gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-[160px] bg-white" data-testid="essl-date-input" />
        <SectionSelect sections={sections} value={section} onChange={setSection} testId="essl-section-filter" />
        <Select value={flag} onValueChange={setFlag}>
          <SelectTrigger className="w-[190px] bg-white" data-testid="essl-flag-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{FILTERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <CollarSelect value={collar} onChange={setCollar} testId="essl-collar-filter" />
        <EsslBackfill onDone={load} />
        <Button variant="outline" className="bg-white" disabled={!data || (!rows.length && !unmatched.length)}
          onClick={() => exportCsv(date, section, flag, rows, unmatched)} data-testid="essl-export-csv-btn"><Download className="w-4 h-4 mr-1.5" />Export CSV</Button>
        {data && <div className="text-xs text-gray-500 ml-auto" data-testid="essl-summary">{data.logs} machine punches · {Object.entries(data.counts || {}).map(([k, n]) => `${FLAG[k]?.[0]}: ${n}`).join(' · ') || 'all matched'}{data.staff_without_code ? ` · ${data.staff_without_code} active staff not found on eSSL (no eSSL Code / Staff ID match in 31 days)` : ''}</div>}
      </div>
      {data?.collar_split && <CollarTiles split={data.collar_split} value={collar} onPick={setCollar} metric="Staff-days shown" testId="essl-collar-tiles" />}
      {data && data.rows.length > 0 && <SearchNote q={search} shown={rows.length} total={data.rows.length} testId="essl-search-note" />}
      {unmatched.length > 0 && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="essl-unmatched">Machine codes not in Staff Master on this day: {unmatched.map((u) => `${u.code}${u.name && u.name !== u.code ? ` ${u.name}` : ''} (${u.count})`).join(', ')} — set it as the staff's <b>eSSL Code</b> (or Staff ID) in Staff Master.</div>}
      <div className="rounded-xl border bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>eSSL first In → last Out</TableHead><TableHead>App first In</TableHead><TableHead>App last Out</TableHead><TableHead>Final In → Out</TableHead><TableHead>Result</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => <Row key={r.staff_id} r={r} busy={busy} onFill={fill} />)}
            {!data && <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-amber-600" /></TableCell></TableRow>}
            {data && data.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8" data-testid="essl-empty">Nothing to show for this day / filter.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default EsslReconcile;
